import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { getTenant, updateTenant } from '../../api/tenants';
import {
  Container,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Terrain as TerrainIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import TenantForm from '../../components/admin/TenantForm';

const EditTenant = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tenant data
  useEffect(() => {
    const fetchTenant = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getTenant(id);
        setTenant(data);
      } catch (error) {
        console.error('Error fetching tenant:', error);
        setError('Failed to load tenant. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [id]);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare tenant data
      const tenantData = {
        name: values.name,
        area: values.area || null,
        registration_number: values.registration_number || null,
        contact_info: values.contact_info || null,
        latitude: values.latitude !== '' && values.latitude != null ? parseFloat(values.latitude) : null,
        longitude: values.longitude !== '' && values.longitude != null ? parseFloat(values.longitude) : null,
        // Super-admin only. The API drops it for everyone else, so sending it
        // unconditionally is safe; omitting it is what silently broke the
        // checkbox, since this payload is built field-by-field rather than
        // spreading `values`.
        is_shadow: Boolean(values.is_shadow),
      };

      // Update tenant
      await updateTenant(id, tenantData);
      
      // Navigate back to tenant list
      navigate('/admin/tenants');
    } catch (error) {
      console.error('Error updating tenant:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to update tenant. Please try again later.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/tenants');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !tenant) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/admin/tenants"
          >
            Back to Tenants
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/dashboard" color="inherit">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/admin/tenants" color="inherit">
            Tenants
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Edit Tenant: {tenant.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TerrainIcon />}
              component={RouterLink}
              to={`/fpa-settings?tab=boundaries&tenant_id=${id}`}
            >
              Boundaries
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<MapIcon />}
              component={RouterLink}
              to={`/admin/tenants/${id}/parcels-map`}
            >
              Parcels Map
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to="/admin/tenants"
            >
              Back to Tenants
            </Button>
          </Box>
        </Box>

        <TenantForm
          initialValues={{
            name: tenant.name,
            area: tenant.area || '',
            registration_number: tenant.registration_number || '',
            contact_info: tenant.contact_info || '',
            latitude: tenant.latitude ?? '',
            longitude: tenant.longitude ?? '',
            // Null for non-super-admins (the API scrubs it), which coerces to
            // false — the checkbox is hidden for them anyway, and the backend
            // ignores the field on their updates.
            is_shadow: Boolean(tenant.is_shadow),
          }}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          error={error}
        />
      </Box>
    </Container>
  );
};

export default EditTenant;